/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef, useCallback } from 'react'
import useZimmerTrayMapLogic from 'lib/hooks/useZimmerTrayMapLogic/useZimmerTrayMapLogic'
import { useSPD } from 'views/SPDLayout/SPD/SPD.context'
import {
  GrayScaleTrayMapProps,
  ScrewAnnotation,
  PlateAnnotation,
  DrillAnnotation,
  TrayData,
  Point,
} from './GrayScale.types'
import useTrayMapLogic from 'views/SPDLayout/SPD/TrayMap/TrayMap.logic'
import { BetterIDTrayScrew } from 'views/SPDLayout/SPD/SPD.types'
import { rodUDIMap } from '../ZimmerRodTrayMap/ZimmerRodTrayMap.types'

import { useAnnotations } from './hooks/useAnnotations'
import { useScaling } from './hooks/useScaling'
import { useSPDScan } from 'views/SPDLayout/SPD/Scan/Scan.context'
import { useAssignedDigitalTrays } from 'views/DigitalTrayMapping/AssignedDigitalTrays/AssignedDigitalTrays.context'
import { useIsMobile } from 'lib/utils/mediaQueries'
import { useIsTablet } from 'assets/utils/mediaQueries'
import { useParams } from 'react-router-dom'
import { extractSubTrayJSONData } from 'lib/utils/extractSubTrayJSONData'

type TrayTemplate = {
  image_url: string
  template_data: TrayData
}

export const useGrayScaleLogic = ({
  isInOR,
  setSelectedPlate,
  setModalOpen,
  trayType,
  setSelectedScrew,
  mapContent,
  isSPD = false,
  templateData: externalTemplate,
}: GrayScaleTrayMapProps = {}) => {
  const { surgeryId } = useParams()
  const abortControllerRef = useRef<AbortController | null>(null)

  const [pendingScrewAsset, setPendingScrewAsset] = useState<any>(null)
  const [universalJsonData, setUniversalJsonData] = useState<TrayData | null>(
    null
  )
  const [backgroundImage, setBackgroundImage] = useState<string | undefined>(
    undefined
  )
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false)
  const [templateError, setTemplateError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [startPoint, setStartPoint] = useState<Point | null>(null)
  const [endPoint, setEndPoint] = useState<Point | null>(null)
  const [isMultiSelectConfirmOpen, setIsMultiSelectConfirmOpen] =
    useState(false)

  const {
    trayPlates,
    trayScrews,
    setTrayPlates,
    setTrayScrews,
    setScrewLoadModalOpen,
    screwLoadModalOpen,
    selectedScrews,
    selectedScrewIndices,
    setSelectedScrewIndices,
    parLevel,
  } = useSPD()
  const {
    usageScrews,
    setUsageScrews,
    usagePlates,
    selectedSubTray,
    setSelectedSubTray,
  } = useAssignedDigitalTrays()
  const { isLoadMultipleBoxVisible, setIsLoadMultipleBoxVisible } = useSPDScan()
  const { trayDetails } = useAssignedDigitalTrays()
  const {
    isLoadingWithUDIs,
    setIsLoadingWithUDIs,
    handleCancelSelection,
    handleLoadingWithUDIs,
  } = useTrayMapLogic()

  const {
    screwAnnotations,
    plateAnnotations,
    drillAnnotations,
    selectedAnnotation,
    setScrewAnnotations,
    setPlateAnnotations,
    setDrillAnnotations,
    setSelectedAnnotation,
    initializeAnnotations,
  } = useAnnotations()

  const {
    originalWidth,
    originalHeight,
    imageScale,
    uiScale,
    setOriginalWidth,
    setOriginalHeight,
    updateScales,
    textScale,
    calculateDynamicScale,
    imagePosition,
    adjustContainerToImageSize,
  } = useScaling()

  const {
    handlePlateCountChange,
    shouldPlateIncreaseBeDisabled,
    setInitialTrayPlates,
    getPlateDI,
  } = useZimmerTrayMapLogic({
    isInOR,
    setSelectedPlate,
    setModalOpen,
    udiMap: rodUDIMap,
    parLevel,
  })

  const dynamicScale = calculateDynamicScale(containerRef)
  const finalScale = Math.min(dynamicScale * uiScale, 1.2)
  const BASE_RADIUS = 25
  const scaledCircleRadius = BASE_RADIUS * dynamicScale
  const isAllScrewsLoaded = screwAnnotations.every((screw) => screw.count > 0)
  const isAllPlatesLoaded =
    trayPlates.every((plate) => plate.plateCount > 0) &&
    drillAnnotations.every((drill) =>
      trayPlates.some((plate) => plate.plateName === drill.label)
    ) &&
    plateAnnotations.every((plate) =>
      trayPlates.some((trayPlate) => trayPlate.plateName === plate.label)
    )
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const isMobileOrTablet = isMobile || isTablet

  const [templateData, setTemplateData] = useState<TrayTemplate | undefined>(
    externalTemplate
  )

  // Handle external template changes
  useEffect(() => {
    if (externalTemplate) {
      setTemplateData(externalTemplate)
      setTemplateError(null)
      setIsLoadingTemplate(false)
    }
  }, [externalTemplate])

  // Fetch template data when trayType changes (only if no external template)
  useEffect(() => {
    if (!trayType || externalTemplate) return

    // Cancel any ongoing fetch request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Reset states when trayType changes
    setTemplateData(undefined)
    setBackgroundImage(undefined)
    setUniversalJsonData(null)
    setTemplateError(null)

    const fetchTemplate = async () => {
      setIsLoadingTemplate(true)

      // Create new AbortController for this request
      const abortController = new AbortController()
      abortControllerRef.current = abortController

      try {
        const encoded = encodeURIComponent(trayType)
        const base = process.env.REACT_APP_DTM_COMPUTER_VISION_API!
        const url = `${base.replace(
          /\/$/,
          ''
        )}/api/tray-templates?tray_type=${encoded}`
        const res = await fetch(url, { signal: abortController.signal })

        if (!res.ok) {
          throw new Error(
            `Failed to fetch template: ${res.status} ${res.statusText}`
          )
        }

        const data = await res.json()

        // Only set data if this request wasn't aborted
        if (!abortController.signal.aborted) {
          setTemplateData(data as TrayTemplate)
        }
      } catch (err) {
        // Don't set error if request was aborted
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }

        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred'
        console.error('GrayScale – failed to fetch template:', err)
        setTemplateError(errorMessage)
        setTemplateData(undefined)
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingTemplate(false)
        }
      }
    }

    fetchTemplate()

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [trayType, externalTemplate])

  // Update background image and JSON data when template changes
  useEffect(() => {
    if (templateData) {
      setBackgroundImage(templateData.image_url)
      setUniversalJsonData(templateData.template_data)
      setTemplateError(null)
    } else if (!isLoadingTemplate && !externalTemplate && !templateError) {
      // Clear previous data when template is not available and not loading
      setBackgroundImage(undefined)
      setUniversalJsonData(null)
    }
  }, [templateData, isLoadingTemplate, externalTemplate, templateError])

  const screwsRemovedByNurse = surgeryId
    ? trayDetails?.removedByNurse?.[surgeryId]
    : []

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement

      if (
        target.closest('.count-controls') ||
        target.closest('.count-circle')
      ) {
        return
      }

      setSelectedAnnotation(null)
    },
    [setSelectedAnnotation]
  )

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClickOutside])

  useEffect(() => {
    if (!containerRef.current || !originalWidth || !originalHeight) return

    const updateContainerSize = () => {
      // Store current dimensions to check if they actually changed
      const prevWidth = containerRef.current?.style.width
      const prevHeight = containerRef.current?.style.height

      // Adjust container to match the exact rendered image size
      adjustContainerToImageSize(containerRef)

      // Only update scales if dimensions actually changed
      if (
        prevWidth !== containerRef.current?.style.width ||
        prevHeight !== containerRef.current?.style.height
      ) {
        const containerWidth = containerRef.current?.clientWidth || 0
        updateScales(containerWidth)
      }
    }

    // Initial update
    updateContainerSize()

    // Use requestAnimationFrame to throttle resize observations
    let rafId: number | null = null
    const throttledResize = () => {
      if (rafId) return
      rafId = requestAnimationFrame(() => {
        updateContainerSize()
        rafId = null
      })
    }

    const resizeObserver = new ResizeObserver(() => {
      throttledResize()
    })

    // Observe the parent element instead of the container itself
    // to avoid feedback loops
    if (containerRef.current.parentElement) {
      resizeObserver.observe(containerRef.current.parentElement)
    }

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      resizeObserver.disconnect()
    }
  }, [originalWidth, originalHeight, containerRef.current])

  useEffect(() => {
    if (mapContent) {
      if (!trayScrews?.length) {
        setTrayScrews(mapContent.screws || [])
      }
      if (!trayPlates?.length) {
        setTrayPlates(mapContent.plates || [])
      }
    }
  }, [mapContent])

  useEffect(() => {
    if (universalJsonData) {
      const trayData = universalJsonData as unknown as {
        [key: string]: {
          screw?: ScrewAnnotation[]
          plate?: PlateAnnotation[]
          drill?: DrillAnnotation[]
          dimensions?: { width: number; height: number }
        }
      }

      const firstKey = Object.keys(trayData)[0]
      const dimensions = firstKey ? trayData[firstKey].dimensions : null
      if (dimensions) {
        setOriginalWidth(dimensions.width)
        setOriginalHeight(dimensions.height)
      }

      initializeAnnotations(trayData, trayScrews, usageScrews)
    }
  }, [universalJsonData, trayScrews, usageScrews])

  const handleScrewRemoval = (
    screwAsset: BetterIDTrayScrew,
    annotation: ScrewAnnotation,
    index: number
  ) => {
    if (setSelectedScrew) {
      setSelectedScrew(screwAsset)
    }

    if (setModalOpen) {
      setModalOpen(true)
    }

    setTrayScrews((prev) =>
      prev.filter(
        (ts) =>
          !(
            ts.row === annotation.row_index &&
            ts.column === annotation.column_label &&
            ts.x === annotation.column_index &&
            ts.label === annotation.label
          )
      )
    )

    setScrewAnnotations((prev) =>
      prev.map((screw, i) => (i === index ? { ...screw, count: 0 } : screw))
    )
  }

  const removeFromRemovedByNurse = (screws: BetterIDTrayScrew[]) => {
    if (!surgeryId || !selectedSubTray) return

    const subTrayDetails = extractSubTrayJSONData(selectedSubTray)

    if (!subTrayDetails || !subTrayDetails.removedByNurse?.[surgeryId]) return

    // Filter out the screws that need to be removed
    const updatedRemovedScrews = subTrayDetails.removedByNurse[
      surgeryId
    ].filter(
      (removedScrew: BetterIDTrayScrew) =>
        !screws.some(
          (screw) =>
            removedScrew.label === screw.label &&
            removedScrew.row === screw.row &&
            removedScrew.x === screw.x
        )
    )

    const newRemovedByNurse = {
      ...subTrayDetails.removedByNurse,
      [surgeryId]: updatedRemovedScrews,
    }

    const updatedScrews = [...(subTrayDetails.screws || []), ...screws]

    const newSubTrayDetails = {
      ...subTrayDetails,
      removedByNurse: newRemovedByNurse,
      screws: updatedScrews,
    }
    const newSubTrayDetailsString = JSON.stringify(newSubTrayDetails)

    setSelectedSubTray((prevSelectedSubTray) =>
      prevSelectedSubTray
        ? {
            ...prevSelectedSubTray,
            productDetails: newSubTrayDetailsString,
          }
        : null
    )

    setTrayScrews((prev) => [...prev, ...screws])

    setScrewAnnotations((prev) =>
      prev.map((annotation) => {
        const matchingScrewBeingRestored = screws.find(
          (screw) =>
            screw.label === annotation.label &&
            screw.row === annotation.row_index &&
            screw.x === annotation.column_index
        )

        if (matchingScrewBeingRestored) {
          return { ...annotation, count: 1 }
        }
        return annotation
      })
    )
  }

  const handleScrewAddition = (
    index: number,
    annotation: ScrewAnnotation,
    screwAsset: BetterIDTrayScrew,
    exists: boolean,
    isRemovedByNurse: boolean
  ) => {
    if (!exists) {
      setPendingScrewAsset(screwAsset)
      if (isSPD) {
        setScrewLoadModalOpen(true)
        setSelectedScrewIndices([index])
      } else {
        if (isRemovedByNurse && isInOR) {
          removeFromRemovedByNurse([screwAsset])
        }

        setIsLoadingWithUDIs(false)
        setScrewAnnotations((prev) =>
          prev.map((screw, i) => (i === index ? { ...screw, count: 1 } : screw))
        )
        setTrayScrews((prev) => [...prev, screwAsset])
        setUsageScrews((prev) =>
          prev.filter(
            (screw) =>
              !(
                screw.label === screwAsset.label &&
                screw.row === screwAsset.row &&
                screw.x === screwAsset.x
              )
          )
        )
      }
    } else {
      setTrayScrews((prev) =>
        prev.filter(
          (ts) =>
            !(
              ts.row === annotation.row_index &&
              ts.column === annotation.column_label &&
              ts.x === annotation.column_index &&
              ts.label === annotation.label
            )
        )
      )

      setScrewAnnotations((prev) =>
        prev.map((screw, i) => (i === index ? { ...screw, count: 0 } : screw))
      )
    }
  }
  const handleScrewClick = (index: number) => {
    const annotation = screwAnnotations[index]

    const isRemovedByNurse = getIsRemovedByNurse(
      annotation.label,
      annotation.row_index,
      annotation.column_index
    )

    const screwAsset = {
      label: annotation.label,
      row: annotation.row_index,
      column: annotation.column_label,
      x: annotation.column_index,
      deviceId: annotation.device_id,
      deviceDescription: annotation.device_description,
    }

    const exists = trayScrews.some(
      (ts) =>
        ts.row === annotation.row_index &&
        ts.column === annotation.column_label &&
        ts.x === annotation.column_index &&
        ts.label === annotation.label
    )

    if (isLoadMultipleBoxVisible) {
      handleLoadingWithUDIs(annotation, index)
      return
    }

    if (annotation.count === 1) {
      if (isInOR) {
        if (selectedScrewIndices.includes(index)) {
          setSelectedScrewIndices((prev) => prev.filter((i) => i !== index))
        } else {
          setSelectedScrewIndices((prev) => [...prev, index])
        }
        return
      }
      handleScrewRemoval(screwAsset, annotation, index)
    } else {
      handleScrewAddition(
        index,
        annotation,
        screwAsset,
        exists,
        isRemovedByNurse
      )
    }
  }

  const handlePlateCountModification = (
    type: 'plate' | 'drill',
    index: number,
    change: number
  ) => {
    const plate = plateAnnotations[index]
    const plateName = plate.label
    const currentPlate = trayPlates.find((p) => p.plateName === plateName)
    const deviceId = plate.device_id
    const deviceDescription = plate.device_description

    // If decreasing count in OR mode
    if (change < 0 && isInOR && currentPlate) {
      if (setSelectedPlate) {
        setSelectedPlate(currentPlate)
      }
      if (setModalOpen) {
        setModalOpen(true)
      }
      return
    }

    // Normal count change (not decreasing in OR)
    const currentCount = currentPlate?.plateCount || 0
    const newCount = Math.max(0, currentCount + change)
    handlePlateCountChange(
      plateName as any,
      newCount,
      deviceId,
      deviceDescription
    )
  }

  const handleDrillCountModification = (index: number, change: number) => {
    const drill = drillAnnotations[index]
    const drillName = drill.label
    const currentDrill = trayPlates.find((p) => p.plateName === drillName)
    const deviceId = drill.device_id
    const deviceDescription = drill.device_description
    // If decreasing count in OR mode
    if (change < 0 && isInOR && currentDrill) {
      if (setSelectedPlate) {
        setSelectedPlate(currentDrill)
      }
      if (setModalOpen) {
        setModalOpen(true)
      }
      return
    }

    // Normal count change (not decreasing in OR)
    const currentCount = currentDrill?.plateCount || 0
    const newCount = Math.max(0, currentCount + change)
    handlePlateCountChange(
      drillName as any,
      newCount,
      deviceId,
      deviceDescription
    )
  }

  const handleCountChange = (
    type: 'plate' | 'drill',
    index: number,
    change: number
  ) => {
    if (type === 'plate') {
      handlePlateCountModification(type, index, change)
    } else {
      handleDrillCountModification(index, change)
    }
  }

  const handleCountCircleClick = (type: 'plate' | 'drill', index: number) => {
    setSelectedAnnotation(`${type}-${index}`)
  }

  const isInSelectionBounds = (
    screwCenter: number[],
    selectionBox: { left: number; top: number; right: number; bottom: number }
  ) => {
    const [x, y] = screwCenter
    const { left, top, right, bottom } = selectionBox
    return x >= left && x <= right && y >= top && y <= bottom
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isLoadingWithUDIs) return

    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return

    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    setIsDragging(true)
    setStartPoint({ x, y })
    setEndPoint({ x, y })
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    setEndPoint({ x, y })
  }

  const handleMouseUp = () => {
    if (!isDragging || !startPoint || !endPoint) return

    const selectionBox = {
      left: Math.min(startPoint.x, endPoint.x),
      top: Math.min(startPoint.y, endPoint.y),
      right: Math.max(startPoint.x, endPoint.x),
      bottom: Math.max(startPoint.y, endPoint.y),
    }

    const selectedIndices = screwAnnotations
      .map((annotation, index) => {
        if (!annotation.img_screw_center) return null
        const [cx, cy] = annotation.img_screw_center
        const scaledX = cx * imageScale
        const scaledY = cy * imageScale

        // Only include screws that are not loaded (count === 0)
        if (annotation.count > 0) return null

        return isInSelectionBounds([scaledX, scaledY], selectionBox)
          ? index
          : null
      })
      .filter((index): index is number => index !== null)

    if (selectedIndices.length > 0) {
      setSelectedScrewIndices(selectedIndices)
      setIsMultiSelectConfirmOpen(true)
    }

    setIsDragging(false)
    setStartPoint(null)
    setEndPoint(null)
  }

  const handleMultiSelectConfirm = () => {
    selectedScrewIndices.forEach((index) => {
      const annotation = screwAnnotations[index]
      const screwAsset = {
        label: annotation.label,
        row: annotation.row_index,
        column: annotation.column_label,
        x: annotation.column_index,
        deviceId: annotation.device_id,
        deviceDescription: annotation.device_description,
      }

      const exists = trayScrews.some(
        (ts) =>
          ts.row === annotation.row_index &&
          ts.column === annotation.column_label &&
          ts.x === annotation.column_index &&
          ts.label === annotation.label
      )

      if (!exists) {
        setTrayScrews((prev) => [...prev, screwAsset])
        setScrewAnnotations((prev) =>
          prev.map((screw, i) => (i === index ? { ...screw, count: 1 } : screw))
        )
      }
    })

    setIsMultiSelectConfirmOpen(false)
    setSelectedScrewIndices([])
    setIsLoadMultipleBoxVisible(false)
  }

  const handleMultiSelectCancel = () => {
    setIsMultiSelectConfirmOpen(false)
    setSelectedScrewIndices([])
  }

  const getSelectedTrayScrews = (selectedIndices: number[]) => {
    return selectedIndices
      .map((index) => {
        const annotation = screwAnnotations[index]

        if (!annotation) return undefined

        const isRemovedByNurse = getIsRemovedByNurse(
          annotation.label,
          annotation.row_index,
          annotation.column_index
        )

        if (isRemovedByNurse) {
          return {
            row: annotation.row_index,
            column: annotation.column_label,
            x: annotation.column_index,
            label: annotation.label,
            deviceId: annotation.device_id,
            deviceDescription: annotation.device_description,
          }
        }
        return trayScrews.find(
          (ts) =>
            ts.row === annotation.row_index &&
            ts.column === annotation.column_label &&
            ts.x === annotation.column_index &&
            ts.label === annotation.label
        )
      })
      .filter(
        (screw): screw is NonNullable<typeof screw> => screw !== undefined
      )
  }
  const loadAllScrews = () => {
    if (isLoadingWithUDIs) return

    const newScrews = screwAnnotations
      .map((annotation): BetterIDTrayScrew | null => {
        if (annotation.count === 0) {
          return {
            label: annotation.label,
            row: annotation.row_index,
            column: annotation.column_label,
            x: annotation.column_index,
            deviceId: annotation.device_id,
            deviceDescription: annotation.device_description,
          } as BetterIDTrayScrew
        }
        return null
      })
      .filter((screw): screw is BetterIDTrayScrew => screw !== null)

    setTrayScrews((prev) => [...prev, ...newScrews])
    setScrewAnnotations((prev) => prev.map((screw) => ({ ...screw, count: 1 })))
  }

  const removeAllScrews = () => {
    setTrayScrews([])
    setScrewAnnotations([])
  }

  const increaseAllPlatesBy = (amount: number) => {
    if (amount <= 0) return

    const updatedPlates = [...trayPlates]
    const allAnnotations = [...plateAnnotations, ...drillAnnotations]
    const newPlateAnnotations = [...plateAnnotations]
    const newDrillAnnotations = [...drillAnnotations]

    allAnnotations.forEach((annotation) => {
      const plateName = annotation.label
      const currentPlate = trayPlates.find((p) => p.plateName === plateName)
      // Set directly to par level instead of incrementing by amount
      const newCount = parLevel

      if (currentPlate) {
        const plateIndex = updatedPlates.findIndex(
          (p) => p.plateName === plateName
        )
        if (plateIndex !== -1) {
          updatedPlates[plateIndex] = {
            ...updatedPlates[plateIndex],
            plateCount: newCount,
          }
        }
      } else if (newCount > 0) {
        updatedPlates.push({
          deviceId: annotation.device_id,
          deviceDescription: annotation.device_description,
          plateName: plateName,
          plateCount: newCount,
        })
      }

      // Update the correct annotation array based on type
      const isPlate = plateAnnotations.some((a) => a.label === plateName)
      if (isPlate) {
        const plateIndex = newPlateAnnotations.findIndex(
          (a) => a.label === plateName
        )
        if (plateIndex !== -1) {
          newPlateAnnotations[plateIndex] = {
            ...newPlateAnnotations[plateIndex],
            count: newCount,
          }
        }
      } else {
        const drillIndex = newDrillAnnotations.findIndex(
          (a) => a.label === plateName
        )
        if (drillIndex !== -1) {
          newDrillAnnotations[drillIndex] = {
            ...newDrillAnnotations[drillIndex],
            count: newCount,
          }
        }
      }
    })

    // Update state with the new annotations
    setPlateAnnotations(newPlateAnnotations)
    setDrillAnnotations(newDrillAnnotations)

    if (!isSPD) {
      setTrayPlates(updatedPlates)
    }
  }

  const decreaseAllPlatesBy = (amount: number) => {
    if (amount <= 0) return

    const updatedPlates = [...trayPlates]
    const allAnnotations = [...plateAnnotations, ...drillAnnotations]
    const updatedAnnotations = [...plateAnnotations, ...drillAnnotations]

    allAnnotations.forEach((annotation, index) => {
      const plateName = annotation.label
      const currentPlate = trayPlates.find((p) => p.plateName === plateName)
      const currentCount = currentPlate?.plateCount || 0
      const newCount = Math.max(currentCount - amount, 0)

      if (isInOR) return

      const plateIndex = updatedPlates.findIndex(
        (p) => p.plateName === plateName
      )
      if (plateIndex !== -1) {
        if (newCount === 0) {
          updatedPlates.splice(plateIndex, 1)
        } else {
          updatedPlates[plateIndex] = {
            ...updatedPlates[plateIndex],
            plateCount: newCount,
          }
        }
      }

      updatedAnnotations[index] = {
        ...updatedAnnotations[index],
        count: newCount,
      }
    })

    if (!isInOR) {
      setTrayPlates(updatedPlates)
    }
  }

  const shouldPlateOrDrillIncreaseBeDisabled = (label: string) => {
    if (shouldPlateIncreaseBeDisabled(label)) {
      return true
    }

    if (isInOR) {
      const usagePlate = usagePlates.find(
        (trayPlate) => trayPlate.plateName === label
      )
      if (!usagePlate) return true
    }

    return false
  }

  const getIsRemovedByNurse = (
    label: string,
    row_index: number,
    column_index: number
  ) => {
    return !!screwsRemovedByNurse?.some(
      (screw) =>
        screw.label === label &&
        screw.row === row_index &&
        screw.x === column_index
    )
  }

  return {
    containerRef,
    backgroundImage,
    pendingScrewAsset,
    screwLoadModalOpen,
    trayScrews,
    trayPlates,
    screwAnnotations,
    plateAnnotations,
    drillAnnotations,
    selectedAnnotation,
    selectedScrews,
    setSelectedAnnotation,
    initializeAnnotations,
    originalWidth,
    originalHeight,
    imageScale,
    uiScale,
    textScale,
    calculateDynamicScale,
    updateScales,
    shouldPlateIncreaseBeDisabled,
    setInitialTrayPlates,
    getPlateDI,
    handleScrewClick,
    handleCountChange,
    handleCountCircleClick,
    handleCancelSelection,
    isDragging,
    startPoint,
    endPoint,
    selectedScrewIndices,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    isMultiSelectConfirmOpen,
    handleMultiSelectConfirm,
    handleMultiSelectCancel,
    isLoadingWithUDIs,
    usageScrews,
    getSelectedTrayScrews,
    loadAllScrews,
    increaseAllPlatesBy,
    decreaseAllPlatesBy,
    removeAllScrews,
    isAllScrewsLoaded,
    isAllPlatesLoaded,
    handleClickOutside,
    finalScale,
    scaledCircleRadius,
    dynamicScale,
    BASE_RADIUS,
    shouldPlateOrDrillIncreaseBeDisabled,
    isMobileOrTablet,
    imagePosition,
    getIsRemovedByNurse,
    templateData,
    isLoadingTemplate,
    templateError,
  }
}

export default useGrayScaleLogic
